import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const AUTOPLAY_MS = 6500;
const DEFERRED_MEDIA_LOAD_MS = 1200;

const defaultSlides = [
    {
        image: '/images/bg/bg-image-15.webp',
        avif: '/images/bg/bg-image-15.avif',
        alt: 'Motorówka BlackWave widziana z góry na wodzie w Augustowie',
    },
    {
        image: '/images/bg/6-hero.webp',
        avif: '/images/bg/6-hero.avif',
        alt: 'Łódź motorowa BlackWave podczas rejsu po augustowskich jeziorach',
    },
    {
        image: '/images/bg/bg-image-6.webp',
        avif: '/images/bg/bg-image-6.avif',
        alt: 'Motorówka TEXAS dostępna w wypożyczalni BlackWave w Augustowie',
    },
];

const HeroCarousel = ({ slides = defaultSlides }) => {
    const safeSlides = useMemo(() => slides.filter((slide) => slide?.image), [slides]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [deferredMediaReady, setDeferredMediaReady] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);
    const [pageVisible, setPageVisible] = useState(() => (typeof document === 'undefined' ? true : !document.hidden));

    const slideCount = safeSlides.length;

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const syncPreference = () => setReducedMotion(mediaQuery.matches);

        syncPreference();
        if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', syncPreference);
        else mediaQuery.addListener?.(syncPreference);

        return () => {
            if (mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', syncPreference);
            else mediaQuery.removeListener?.(syncPreference);
        };
    }, []);


    useEffect(() => {
        const handleVisibilityChange = () => setPageVisible(!document.hidden);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    useEffect(() => {
        const timer = window.setTimeout(() => setDeferredMediaReady(true), DEFERRED_MEDIA_LOAD_MS);
        return () => window.clearTimeout(timer);
    }, []);

    const goTo = useCallback((index) => {
        if (!slideCount) return;
        setActiveIndex((index + slideCount) % slideCount);
    }, [slideCount]);

    const previous = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);
    const next = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);

    useEffect(() => {
        if (slideCount < 2 || paused || reducedMotion || !pageVisible) return undefined;

        const timer = window.setInterval(() => {
            setActiveIndex((current) => (current + 1) % slideCount);
        }, AUTOPLAY_MS);

        return () => window.clearInterval(timer);
    }, [pageVisible, paused, reducedMotion, slideCount]);

    if (!slideCount) return null;

    return (
        <section
            className="blackwave-hero"
            aria-roledescription="carousel"
            aria-label="BlackWave — wynajem motorówek w Augustowie"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocusCapture={() => setPaused(true)}
            onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
            }}
        >
            <div className="blackwave-hero__media" aria-hidden="true">
                {safeSlides.map((slide, index) => {
                    const shouldLoad = index === 0 || deferredMediaReady || index === activeIndex;

                    return (
                        <div
                            key={slide.image}
                            className={`blackwave-hero__slide ${index === activeIndex ? 'is-active' : ''}`}
                        >
                            {shouldLoad && (
                                <picture>
                                    {slide.avif && <source srcSet={`${process.env.PUBLIC_URL}${slide.avif}`} type="image/avif" />}
                                    <img
                                        className="blackwave-hero__image"
                                        src={`${process.env.PUBLIC_URL}${slide.image}`}
                                        alt=""
                                        width="1920"
                                        height="1080"
                                        loading={index === 0 ? 'eager' : 'lazy'}
                                        decoding={index === 0 ? 'sync' : 'async'}
                                        fetchPriority={index === 0 ? 'high' : 'low'}
                                    />
                                </picture>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="blackwave-hero__overlay" aria-hidden="true" />

            <div className="container blackwave-hero__container">
                <div className="row align-items-center h-100">
                    <div className="col-xl-7 col-lg-8 col-md-10">
                        <div className="blackwave-hero__content">
                            <p className="blackwave-hero__eyebrow">BLACKWAVE · AUGUSTÓW</p>
                            <h1 className="blackwave-hero__title">
                                BlackWave
                                <span>Wynajem motorówek w Augustowie</span>
                            </h1>
                            <p className="blackwave-hero__description">
                                Profesjonalny czarter łodzi motorowych TEXAS na Jeziorze Białym Augustowskim i Jeziorze Necko — ze sternikiem lub bez, zależnie od wybranej jednostki i warunków wynajmu.
                            </p>
                            <div className="blackwave-hero__actions">
                                <a className="btn-default" href="#pricing">Zobacz łodzie</a>
                                <a className="blackwave-hero__secondary" href="tel:+48668650693">+48 668 650 693</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {slideCount > 1 && (
                <div className="blackwave-hero__controls" aria-label="Sterowanie karuzelą">
                    <button type="button" className="blackwave-hero__arrow" onClick={previous} aria-label="Poprzednie zdjęcie">
                        <FiChevronLeft aria-hidden="true" />
                    </button>
                    <div className="blackwave-hero__dots" role="group" aria-label="Wybierz zdjęcie">
                        {safeSlides.map((slide, index) => (
                            <button
                                key={`dot-${slide.image}`}
                                type="button"
                                className={`blackwave-hero__dot ${index === activeIndex ? 'is-active' : ''}`}
                                onClick={() => goTo(index)}
                                aria-label={`Pokaż zdjęcie ${index + 1} z ${slideCount}`}
                                aria-current={index === activeIndex ? 'true' : undefined}
                            />
                        ))}
                    </div>
                    <button type="button" className="blackwave-hero__arrow" onClick={next} aria-label="Następne zdjęcie">
                        <FiChevronRight aria-hidden="true" />
                    </button>
                </div>
            )}

            <span className="sr-only" aria-live="polite">
                Zdjęcie {activeIndex + 1} z {slideCount}: {safeSlides[activeIndex]?.alt}
            </span>
        </section>
    );
};

export default HeroCarousel;
